(() => {
  const DEBUG =
    new URLSearchParams(window.location.search).has('aura_gallery_debug');

  const debug = (...args) => {
    if (DEBUG) {
      console.log('[Aura Gallery Analytics]', ...args);
    }
  };

  /**
   * Publish to Shopify's customer-event bus.
   *
   * We retry briefly because the storefront can finish parsing
   * before Shopify.analytics becomes available.
   */
  function publishToShopify(eventName, payload, attempt = 0) {
    const publish = window.Shopify?.analytics?.publish;

    if (typeof publish === 'function') {
      try {
        publish.call(
          window.Shopify.analytics,
          `aura:${eventName}`,
          payload
        );

        debug(`Published aura:${eventName}`, payload);
      } catch (error) {
        debug('Shopify publish error', error);
      }

      return;
    }

    if (attempt < 10) {
      window.setTimeout(() => {
        publishToShopify(eventName, payload, attempt + 1);
      }, 250);
    } else {
      debug(
        'Shopify.analytics.publish was not available',
        eventName,
        payload
      );
    }
  }

  /**
   * This intentionally does NOT install a Google tag.
   *
   * If the existing Google & YouTube integration exposes gtag()
   * on the storefront, we can use that existing tag.
   *
   * If it does not exist, nothing happens here.
   */
function publishToExistingGoogleTag(
  eventName,
  payload,
  attempt = 0
) {
  if (typeof window.gtag === 'function') {
    try {
      window.gtag('event', eventName, payload);

      debug(
        `Sent ${eventName} to existing gtag()`,
        payload
      );
    } catch (error) {
      debug('Existing gtag() send error', error);
    }

    return;
  }

  /*
   * Google & YouTube can expose gtag() slightly after
   * the storefront finishes loading.
   *
   * Retry for up to ~10 seconds so initial gallery
   * events such as media position 1 are not lost.
   */
  if (attempt < 40) {
    window.setTimeout(() => {
      publishToExistingGoogleTag(
        eventName,
        payload,
        attempt + 1
      );
    }, 250);

    return;
  }

  debug(
    `Existing storefront gtag() unavailable after retry; ${eventName} not sent directly to GA4`,
    payload
  );
}

  function emit(eventName, payload) {
    const cleanPayload = {};

    Object.entries(payload).forEach(([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        value !== ''
      ) {
        cleanPayload[key] = value;
      }
    });

    debug(`EVENT: ${eventName}`, cleanPayload);

    publishToShopify(eventName, cleanPayload);
    publishToExistingGoogleTag(eventName, cleanPayload);
  }

  function initGallery(gallery) {
    if (gallery.dataset.auraGalleryAnalyticsReady === 'true') {
      return;
    }

    gallery.dataset.auraGalleryAnalyticsReady = 'true';

    const productId =
      gallery.dataset.auraProductId || '';

    const productHandle =
      gallery.dataset.auraProductHandle || '';

    const presentation =
      gallery.dataset.presentation || '';

    /*
     * The first slideshow-component inside the product gallery
     * is the actual PDP media carousel.
     */
    const slideshow =
      gallery.querySelector('slideshow-component');

    const slides = slideshow
      ? Array.from(
          slideshow.querySelectorAll(
            'slideshow-slides > slideshow-slide'
          )
        )
      : [];

    /*
     * Build the authoritative gallery order from Shopify's
     * existing product-media data-media-id values.
     */
    const orderedMediaIds = slides
      .map((slide) => {
        const media = slide.querySelector(
          '.product-media[data-media-id]'
        );

        return media?.dataset.mediaId || '';
      })
      .filter(Boolean);

    /*
     * One gallery_media_view per media item per page load.
     *
     * Example:
     * 1 -> 2 -> 3 -> 2 -> 3
     *
     * Media 2 and 3 are NOT counted twice.
     */
    const viewedMediaIds = new Set();

    /*
     * Same concept for video engagement milestones.
     */
    const firedVideoEvents = new Set();

    /*
     * Prevent attaching video listeners more than once.
     */
    const boundVideos = new WeakSet();

    function getMediaType(mediaElement) {
      const container =
        mediaElement.closest('.product-media-container');

      if (!container) {
        return 'unknown';
      }

      if (
        container.classList.contains(
          'product-media-container--external_video'
        )
      ) {
        return 'external_video';
      }

      if (
        container.classList.contains(
          'product-media-container--video'
        )
      ) {
        return 'video';
      }

      if (
        container.classList.contains(
          'product-media-container--model'
        )
      ) {
        return 'model';
      }

      if (
        container.classList.contains(
          'product-media-container--image'
        )
      ) {
        return 'image';
      }

      return 'unknown';
    }

    function getMediaInfo(mediaElement) {
      const mediaId =
        mediaElement?.dataset?.mediaId || '';

      const mediaPosition =
        orderedMediaIds.indexOf(mediaId) + 1;

      return {
        product_id: productId,
        product_handle: productHandle,

        media_id: mediaId,
        media_type: getMediaType(mediaElement),

        media_position:
          mediaPosition > 0
            ? mediaPosition
            : undefined,

        media_total:
          orderedMediaIds.length || undefined,

        gallery_presentation: presentation,

        page_path: window.location.pathname
      };
    }

    function trackMediaView(mediaElement) {
      if (!mediaElement) {
        return;
      }

      const info = getMediaInfo(mediaElement);

      if (!info.media_id) {
        return;
      }

      if (viewedMediaIds.has(info.media_id)) {
        return;
      }

      viewedMediaIds.add(info.media_id);

      emit('gallery_media_view', info);
    }

    function trackSlide(slide) {
      const mediaElement =
        slide.querySelector(
          '.product-media[data-media-id]'
        );

      trackMediaView(mediaElement);
    }

    /*
     * -------------------------------------------------------
     * MOBILE / CAROUSEL TRACKING
     * -------------------------------------------------------
     *
     * Tinker marks the current slide:
     *
     * aria-hidden="false"
     *
     * We watch for that attribute changing.
     */
    if (slides.length) {
      const activeSlideObserver =
        new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            const slide = mutation.target;

            if (
              slide instanceof HTMLElement &&
              slide.getAttribute('aria-hidden') === 'false'
            ) {
              trackSlide(slide);
            }
          });
        });

      slides.forEach((slide) => {
        activeSlideObserver.observe(slide, {
          attributes: true,
          attributeFilter: ['aria-hidden']
        });
      });

      /*
       * Count the initial hero slide.
       */
      const initialActiveSlide =
        slides.find(
          (slide) =>
            slide.getAttribute('aria-hidden') === 'false'
        );

      if (initialActiveSlide) {
        trackSlide(initialActiveSlide);
      }
    }

    /*
     * -------------------------------------------------------
     * DESKTOP GRID TRACKING
     * -------------------------------------------------------
     *
     * If the gallery is displayed as a grid on desktop,
     * there is no "active slide".
     *
     * Count a media item once approximately 60% of it has
     * entered the viewport.
     */
    const gridMedia = Array.from(
      gallery.querySelectorAll(
        '.media-gallery__grid .product-media[data-media-id]'
      )
    );

    if (
      gridMedia.length &&
      'IntersectionObserver' in window
    ) {
      const gridObserver =
        new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (
                entry.isIntersecting &&
                entry.intersectionRatio >= 0.6
              ) {
                trackMediaView(entry.target);
              }
            });
          },
          {
            threshold: [0.6]
          }
        );

      gridMedia.forEach((mediaElement) => {
        gridObserver.observe(mediaElement);
      });
    }

    /*
     * -------------------------------------------------------
     * VIDEO TRACKING
     * -------------------------------------------------------
     */

    function emitVideoOnce(
      key,
      eventName,
      video,
      mediaElement,
      extra = {}
    ) {
      if (firedVideoEvents.has(key)) {
        return;
      }

      firedVideoEvents.add(key);

      const info = getMediaInfo(mediaElement);

      emit(eventName, {
        ...info,

        video_duration:
          Number.isFinite(video.duration)
            ? Math.round(video.duration)
            : undefined,

        ...extra
      });
    }

    function bindVideo(video) {
      if (boundVideos.has(video)) {
        return;
      }

      const mediaElement =
        video.closest(
          '.product-media[data-media-id]'
        );

      if (!mediaElement) {
        return;
      }

      boundVideos.add(video);

      const mediaId =
        mediaElement.dataset.mediaId || '';

      /*
       * Actual playback begins.
       */
      video.addEventListener('play', () => {
        emitVideoOnce(
          `${mediaId}:play`,
          'gallery_video_play',
          video,
          mediaElement
        );
      });

      /*
       * Watch percentage viewed.
       */
      video.addEventListener('timeupdate', () => {
        if (
          !Number.isFinite(video.duration) ||
          video.duration <= 0
        ) {
          return;
        }

        const percent =
          (video.currentTime / video.duration) * 100;

        [25, 50, 75].forEach((milestone) => {
          if (percent < milestone) {
            return;
          }

          emitVideoOnce(
            `${mediaId}:${milestone}`,
            `gallery_video_${milestone}`,
            video,
            mediaElement,
            {
              video_percent: milestone
            }
          );
        });
      });

      /*
       * Video completed naturally.
       */
      video.addEventListener('ended', () => {
        emitVideoOnce(
          `${mediaId}:complete`,
          'gallery_video_complete',
          video,
          mediaElement,
          {
            video_percent: 100
          }
        );
      });

      debug(
        'Video listeners attached',
        mediaId
      );
    }

    /*
     * Some videos already exist when the tracker starts.
     */
    gallery
      .querySelectorAll('video')
      .forEach(bindVideo);

    /*
     * Your product videos use deferred-media, meaning the
     * real <video> can be inserted only after interaction.
     *
     * Watch the gallery for newly-created video elements.
     */
    const videoDomObserver =
      new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) {
              return;
            }

            if (node.matches('video')) {
              bindVideo(node);
            }

            node
              .querySelectorAll?.('video')
              .forEach(bindVideo);
          });
        });
      });

    videoDomObserver.observe(gallery, {
      childList: true,
      subtree: true
    });

    debug('Gallery initialized', {
      productId,
      productHandle,
      presentation,
      mediaTotal: orderedMediaIds.length
    });
  }

  function initAllGalleries() {
    document
      .querySelectorAll(
        'media-gallery[data-aura-product-id]'
      )
      .forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initAllGalleries
    );
  } else {
    initAllGalleries();
  }

  /*
   * Also works inside Shopify theme editor previews
   * when a section reloads.
   */
  document.addEventListener(
    'shopify:section:load',
    initAllGalleries
  );
})();